import { Area } from 'react-easy-crop';

export const NATURAL_ASPECT = 'natural' as const;
export type AspectRatioChoice = number | typeof NATURAL_ASPECT;

export const ASPECT_RATIO_OPTIONS: Array<{ label: string; value: number }> = [
  { label: '1:1', value: 1 / 1 },
  { label: '4:3', value: 4 / 3 },
  { label: '16:9', value: 16 / 9 },
  { label: '9:3', value: 9 / 3 },
  { label: '3:4', value: 3 / 4 },
];

export function normalizeImagePath(rawUrl: string): string {
  if (!rawUrl) return rawUrl;
  if (rawUrl.startsWith('http')) return rawUrl;
  if (rawUrl.startsWith('/api/uploads/')) return rawUrl;
  if (rawUrl.startsWith('/uploads/')) return `/api${rawUrl}`;
  return rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`;
}

export function toAdminImageUrl(path: string): string {
  if (!path || path.startsWith('http')) return path;
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
  const serverBase = apiBase.replace(/\/api\/?$/, '');
  const normalized = normalizeImagePath(path);
  return `${serverBase}${normalized}`;
}

export function isFullImageCrop(
  pixelCrop: Area,
  imageWidth: number,
  imageHeight: number
): boolean {
  const tolerance = 2;
  return (
    Math.abs(pixelCrop.x) <= tolerance &&
    Math.abs(pixelCrop.y) <= tolerance &&
    Math.abs(pixelCrop.width - imageWidth) <= tolerance &&
    Math.abs(pixelCrop.height - imageHeight) <= tolerance
  );
}

export function validateImageFile(file: File, maxSizeMb: number): string | null {
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    return 'Type de fichier non supporté. Seules les images (JPEG, PNG, GIF, WEBP) sont acceptées.';
  }

  const maxInputSizeMb = Math.max(maxSizeMb, 20);
  const maxSizeBytes = maxInputSizeMb * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return `La taille du fichier dépasse ${maxInputSizeMb}MB.`;
  }

  return null;
}

export function createImageElement(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', reject);
    image.src = url;
  });
}

export async function getCroppedImageBlob(
  imageSrc: string,
  pixelCrop: Area,
  outputType: string,
  maxSizeMb: number
): Promise<Blob> {
  const image = await createImageElement(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Impossible de traiter cette image.');

  canvas.width = Math.max(1, Math.floor(pixelCrop.width));
  canvas.height = Math.max(1, Math.floor(pixelCrop.height));
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    canvas.width,
    canvas.height
  );

  const targetSizeBytes = maxSizeMb * 1024 * 1024;
  const isJpeg = outputType === 'image/jpeg';
  let blob: Blob | null = null;

  if (isJpeg) {
    for (let quality = 0.98; quality >= 0.7; quality -= 0.05) {
      blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, outputType, quality)
      );
      if (!blob) break;
      if (blob.size <= targetSizeBytes) break;
    }
  } else {
    blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, outputType)
    );
    if (blob && blob.size > targetSizeBytes) {
      throw new Error(
        `L'image recadrée dépasse ${maxSizeMb} Mo. Réduisez la zone ou choisissez un format plus léger.`
      );
    }
  }

  if (!blob) throw new Error('Compression impossible.');
  return blob;
}

export async function uploadImageBlob(blob: Blob, mimeType: string): Promise<string> {
  const extension =
    mimeType === 'image/png'
      ? 'png'
      : mimeType === 'image/webp'
        ? 'webp'
        : mimeType === 'image/gif'
          ? 'gif'
          : 'jpg';
  const file =
    blob instanceof File
      ? blob
      : new File([blob], `image-edited.${extension}`, { type: mimeType });
  const formData = new FormData();
  formData.append('image', file);

  const token = localStorage.getItem('admin_token');
  const response = await fetch(`${process.env.REACT_APP_API_URL}/images/single`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || "Erreur lors de l'upload");
  }

  const result = await response.json();
  return result.data.image.url as string;
}

export function resolveOutputMimeType(file: File): string {
  if (file.type === 'image/png' || file.type === 'image/webp' || file.type === 'image/gif') {
    return file.type;
  }
  return 'image/jpeg';
}
